import { FileSource, PMTiles } from "pmtiles";
import { getStoredMapPack } from "../../../../lib/offline-maps";
import { mapPackById, mapPackForPoint } from "../../../../../shared/ecuador-map-packs";
import type { GeoArResult } from "../lab-v14/types/GeoArResult";

const MOUNTAIN_TYPES = new Set(["MOUNTAIN", "VOLCANO", "SUMMIT", "HILL"]);
const MIN_VALID_ELEVATION_M = -500;
const MAX_VALID_ELEVATION_M = 9_000;
const TERRARIUM_TILE_SIZE = 256;

type ElevationTarget = {
  latitude: number;
  longitude: number;
  packIds: string[];
};

export type ResolvedElevationV4 = {
  elevationM: number;
  source: string;
  status: "published" | "estimated-dem-v4";
};

const targetRegistry = new Map<string, ElevationTarget>();
const archiveCache = new Map<string, Promise<PMTiles | null>>();
const elevationCache = new Map<string, Promise<ResolvedElevationV4 | null>>();

function validElevation(value: unknown) {
  if (value === null || value === undefined || value === "") return null;
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric < MIN_VALID_ELEVATION_M || numeric > MAX_VALID_ELEVATION_M) return null;
  return numeric;
}

function normalizeFeatureId(value: unknown) {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

export function registerElevationTargetV4(input: {
  featureUuid: string;
  latitude: number;
  longitude: number;
  packIds?: readonly string[];
}) {
  const featureUuid = normalizeFeatureId(input.featureUuid);
  if (!featureUuid || !Number.isFinite(input.latitude) || !Number.isFinite(input.longitude)) return;
  targetRegistry.set(featureUuid, {
    latitude: input.latitude,
    longitude: input.longitude,
    packIds: [...new Set((input.packIds ?? []).filter(Boolean))],
  });
}

export function clearElevationResolverV4RuntimeCache() {
  targetRegistry.clear();
  elevationCache.clear();
  archiveCache.clear();
}

export function terrariumElevationFromRgb(red: number, green: number, blue: number) {
  return red * 256 + green + blue / 256 - 32768;
}

export function lonLatToTerrainTilePixel(longitude: number, latitude: number, zoom: number) {
  const safeLatitude = Math.max(-85.05112878, Math.min(85.05112878, latitude));
  const scale = 2 ** zoom;
  const xFloat = ((longitude + 180) / 360) * scale;
  const radians = (safeLatitude * Math.PI) / 180;
  const yFloat = ((1 - Math.asinh(Math.tan(radians)) / Math.PI) / 2) * scale;
  const tileX = Math.max(0, Math.min(scale - 1, Math.floor(xFloat)));
  const tileY = Math.max(0, Math.min(scale - 1, Math.floor(yFloat)));
  const pixelX = Math.max(0, Math.min(TERRARIUM_TILE_SIZE - 1, Math.floor((xFloat - Math.floor(xFloat)) * TERRARIUM_TILE_SIZE)));
  const pixelY = Math.max(0, Math.min(TERRARIUM_TILE_SIZE - 1, Math.floor((yFloat - Math.floor(yFloat)) * TERRARIUM_TILE_SIZE)));
  return { tileX, tileY, pixelX, pixelY };
}

async function openTerrainArchive(terrainFile: string) {
  let promise = archiveCache.get(terrainFile);
  if (!promise) {
    promise = (async () => {
      const stored = await getStoredMapPack(terrainFile).catch(() => undefined);
      if (!stored?.blob?.size || typeof File === "undefined") return null;
      const file = new File([stored.blob], terrainFile, { type: "application/vnd.pmtiles" });
      return new PMTiles(new FileSource(file));
    })();
    archiveCache.set(terrainFile, promise);
  }
  return promise;
}

async function loadImagePixels(data: ArrayBuffer) {
  if (typeof document === "undefined") return null;
  const blob = new Blob([data], { type: "image/png" });
  let drawable: ImageBitmap | HTMLImageElement | null = null;
  let revokeUrl: string | null = null;
  try {
    if (typeof createImageBitmap === "function") {
      drawable = await createImageBitmap(blob);
    } else {
      const image = new Image();
      revokeUrl = URL.createObjectURL(blob);
      await new Promise<void>((resolve, reject) => {
        image.onload = () => resolve();
        image.onerror = () => reject(new Error("THERA_DEM_IMAGE_DECODE_FAILED"));
        image.src = revokeUrl!;
      });
      drawable = image;
    }

    const width = "width" in drawable ? drawable.width : TERRARIUM_TILE_SIZE;
    const height = "height" in drawable ? drawable.height : TERRARIUM_TILE_SIZE;
    if (!width || !height) return null;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) return null;
    context.drawImage(drawable, 0, 0);
    return { width, height, data: context.getImageData(0, 0, width, height).data };
  } finally {
    if (drawable && "close" in drawable && typeof drawable.close === "function") drawable.close();
    if (revokeUrl) URL.revokeObjectURL(revokeUrl);
  }
}

function localTerrariumEstimate(
  rgba: Uint8ClampedArray,
  width: number,
  height: number,
  pixelX: number,
  pixelY: number,
) {
  const samples: number[] = [];
  const offsets = [[0, 0], [-1, 0], [1, 0], [0, -1], [0, 1]] as const;
  for (const [dx, dy] of offsets) {
    const x = Math.max(0, Math.min(width - 1, pixelX + dx));
    const y = Math.max(0, Math.min(height - 1, pixelY + dy));
    const offset = (y * width + x) * 4;
    const elevation = terrariumElevationFromRgb(rgba[offset], rgba[offset + 1], rgba[offset + 2]);
    if (Number.isFinite(elevation) && elevation >= MIN_VALID_ELEVATION_M && elevation <= MAX_VALID_ELEVATION_M) {
      samples.push(elevation);
    }
  }
  if (!samples.length) return null;
  samples.sort((left, right) => left - right);
  return samples[Math.floor(samples.length / 2)];
}

function terrainFilesForTarget(target: ElevationTarget) {
  const preferred = mapPackForPoint(target.longitude, target.latitude);
  const orderedIds = [preferred?.id ?? null, ...target.packIds]
    .filter((value): value is string => Boolean(value));
  const files: string[] = [];
  for (const id of orderedIds) {
    const terrainFile = mapPackById(id)?.terrainFile ?? null;
    if (terrainFile && !files.includes(terrainFile)) files.push(terrainFile);
  }
  return files;
}

async function sampleTerrainElevation(target: ElevationTarget): Promise<ResolvedElevationV4 | null> {
  for (const terrainFile of terrainFilesForTarget(target)) {
    const archive = await openTerrainArchive(terrainFile).catch(() => null);
    if (!archive) continue;
    try {
      const header = await archive.getHeader();
      const zoom = Math.max(header.minZoom, Math.min(header.maxZoom, 12));
      const tile = lonLatToTerrainTilePixel(target.longitude, target.latitude, zoom);
      const response = await archive.getZxy(zoom, tile.tileX, tile.tileY);
      if (!response?.data) continue;
      const decoded = await loadImagePixels(response.data);
      if (!decoded) continue;
      const scaleX = decoded.width / TERRARIUM_TILE_SIZE;
      const scaleY = decoded.height / TERRARIUM_TILE_SIZE;
      const estimate = localTerrariumEstimate(
        decoded.data,
        decoded.width,
        decoded.height,
        Math.floor(tile.pixelX * scaleX),
        Math.floor(tile.pixelY * scaleY),
      );
      if (estimate === null) continue;
      return {
        elevationM: Math.round(estimate),
        source: `THERA DEM regional · ${terrainFile}`,
        status: "estimated-dem-v4",
      };
    } catch {
      // Try the next downloaded terrain pack. Never invent a public altitude.
    }
  }
  return null;
}

async function resolveRegisteredTarget(featureUuid: string) {
  const target = targetRegistry.get(featureUuid);
  if (!target) return null;
  const key = `${featureUuid}|${target.latitude.toFixed(5)}|${target.longitude.toFixed(5)}`;
  let promise = elevationCache.get(key);
  if (!promise) {
    promise = sampleTerrainElevation(target);
    elevationCache.set(key, promise);
  }
  return promise;
}

export async function resolveGeoArResultElevationV4(result: GeoArResult | null | undefined): Promise<GeoArResult | null> {
  if (!result) return null;
  if (!MOUNTAIN_TYPES.has(String(result.feature_type))) return result;

  const existing = validElevation(result.feature_altitude_m);
  if (existing !== null) {
    return {
      ...result,
      feature_altitude_m: existing,
      feature_altitude_source: result.feature_altitude_source ?? "metadato cartografico",
      feature_altitude_status: result.feature_altitude_status ?? "published",
    };
  }

  const featureUuid = normalizeFeatureId(result.feature_uuid);
  if (!featureUuid) return result;
  const resolved = await resolveRegisteredTarget(featureUuid);
  if (!resolved) return result;
  return {
    ...result,
    feature_altitude_m: resolved.elevationM,
    feature_altitude_source: resolved.source,
    feature_altitude_status: resolved.status,
  };
}
